import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import Modal from "components/shared/Modal";
import {
  PROPERTY_STATUS_OPTIONS,
  usePropertyActivationCheck,
  useUpdatePropertyStatus,
} from "./hooks/usePropertyData";
import type { PropertyStatus } from "./hooks/usePropertyData";
import { Alert } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Spinner } from "components/ui/spinner";

interface PropertyStatusModalProps {
  show: boolean;
  onHide: () => void;
  propertyExternalId: string;
  currentStatus?: PropertyStatus | null;
}

export default function PropertyStatusModal({
  show,
  onHide,
  propertyExternalId,
  currentStatus,
}: PropertyStatusModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [selectedStatus, setSelectedStatus] = useState<PropertyStatus>("Staging");

  useEffect(() => {
    if (show) {
      setSelectedStatus(currentStatus ?? "Staging");
    }
  }, [show, currentStatus]);

  // The Staging/Archived -> Active guardrail is informational only -- it never blocks the toggle.
  const isActivating = selectedStatus === "Active" && currentStatus !== "Active";
  const {
    data: activationCheck,
    isError: activationCheckError,
    isLoading: activationCheckLoading,
  } = usePropertyActivationCheck(propertyExternalId, show && isActivating);

  const updateStatusMutation = useUpdatePropertyStatus(propertyExternalId);

  const handleClose = () => {
    onHide();
  };

  const handleConfirm = () => {
    updateStatusMutation.mutate(
      { status: selectedStatus },
      {
        onSuccess: () => {
          enqueueSnackbar(`Property status updated to ${selectedStatus}`, { variant: "success" });
          queryClient.invalidateQueries({ queryKey: ["property-config", propertyExternalId] });
          queryClient.invalidateQueries({ queryKey: ["properties"] });
          handleClose();
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.message || error?.message || "Failed to update property status";
          enqueueSnackbar(message, { variant: "error" });
        },
      },
    );
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Update Property Status</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ paddingTop: 20 }}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Current status: <strong>{currentStatus ?? "Staging"}</strong>
          </p>
          <Select
            value={selectedStatus}
            onValueChange={(status) => setSelectedStatus(status as PropertyStatus)}
          >
            <SelectTrigger aria-label="Property status" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_STATUS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isActivating && (
            activationCheckLoading ? (
              <div className="flex items-center gap-1">
                <Spinner />
                <span className="text-sm">Checking existing guest access...</span>
              </div>
            ) : activationCheckError ? (
              <Alert variant="warning">
                Unable to verify existing guest access. You may still proceed,
                but verify access after activation.
              </Alert>
            ) : activationCheck?.recommendation === "reonboardFirst" ? (
              <Alert variant="warning">
                {activationCheck.guestCount} resident(s)/staff have existing access records for this
                property. Recommend running Re-onboard before activating to ensure their access reflects
                current site configuration. You may still proceed without re-onboarding.
              </Alert>
            ) : (
              <Alert variant="info">
                No residents currently have access records for this property. Proceeding will activate it
                with no existing residents provisioned.
              </Alert>
            )
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={updateStatusMutation.isPending || selectedStatus === (currentStatus ?? "Staging")}
        >
          {updateStatusMutation.isPending ? <Spinner className="size-5" /> : "Update Status"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
